import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const getToken = () => localStorage.getItem('token') || '';
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

/**
 * A security deposit is money **reserved, not taken**. Stripe authorizes the card, the bank sets
 * the funds aside, and nothing is charged unless the manager keeps part of it.
 *
 * <p>Two facts drive every screen that reads this module:
 * <ul>
 *   <li><b>Only HELD means the money is really there.</b> A saved card proves the card exists and
 *       nothing else — so CARD_SAVED is not a secured deposit and must never be shown as one.</li>
 *   <li><b>The window is finite.</b> An authorization lapses after about a week, so
 *       {@link Deposit.holdExpiresAt} is a genuine deadline. An ignored deposit releases itself.</li>
 * </ul>
 */
export type DepositStatus =
  | 'PENDING'      // nothing collected yet
  | 'CARD_SAVED'   // card on file, no money reserved
  | 'HELD'         // money genuinely reserved — the only state a decision can be made in
  | 'HOLD_FAILED'  // the bank refused; retried hourly
  | 'RELEASED'     // let go deliberately
  | 'CLAIMED'      // part or all captured
  | 'EXPIRED';     // lapsed with nobody deciding

/** Why a manager kept part of a deposit. Stored as a code, rendered in the guest's language. */
export type DepositClaimReason =
  | 'DAMAGE' | 'MISSING_ITEMS' | 'EXTRA_CLEANING'
  | 'SMOKING' | 'EXTRA_GUESTS' | 'LATE_CHECKOUT' | 'OTHER';

/** The order they appear in the manager's picker. */
export const CLAIM_REASONS: DepositClaimReason[] = [
  'DAMAGE', 'MISSING_ITEMS', 'EXTRA_CLEANING', 'SMOKING', 'EXTRA_GUESTS', 'LATE_CHECKOUT', 'OTHER',
];

/** True once the outcome is fixed and no Stripe call can change it. */
export const isSettled = (status: DepositStatus) =>
  status === 'RELEASED' || status === 'CLAIMED' || status === 'EXPIRED';

// ── Manager: per-apartment settings ────────────────────────────────────────

export interface DepositSettings {
  enabled: boolean;
  amount: number | null;
  currency: string;
  /** Days before check-in to reserve the money. Capped below the authorization window. */
  holdLeadDays: number;
  /** Days after check-out the manager has. Drives reminders and the automatic release. */
  decisionDays: number;
  autoRelease: boolean;
  /** False when this apartment has no Stripe account that can currently take a charge. */
  paymentsReady: boolean;
}

export const getDepositSettings = (apartmentId: number) =>
  axios.get<DepositSettings>(`${API_BASE}/apartments/${apartmentId}/deposit`, auth())
    .then(r => r.data);

export const updateDepositSettings = (
  apartmentId: number,
  body: Partial<Pick<DepositSettings, 'enabled' | 'amount' | 'holdLeadDays' | 'decisionDays' | 'autoRelease'>>,
) =>
  axios.put<DepositSettings>(`${API_BASE}/apartments/${apartmentId}/deposit`, body, auth())
    .then(r => r.data);

// ── Manager: the queue ─────────────────────────────────────────────────────

/**
 * One row of the Home queue. Two different jobs share the list — an arrival that is not covered
 * yet, and money already held awaiting a decision — because both are the same question to a
 * manager: what needs me today?
 */
export interface DepositQueueItem {
  depositId: number;
  reservationId: number;
  apartmentName: string;
  guestName: string;
  checkInDate: string;
  checkOutDate: string;
  status: DepositStatus;
  amount: number;
  currency: string;
  /** ARRIVAL before the stay starts, HOLD once money is reserved. Decides which clock is shown. */
  deadlineKind: 'ARRIVAL' | 'HOLD';
  deadlineAt: string | null;
  lastHoldError: string | null;
}

export const getOpenDeposits = () =>
  axios.get<DepositQueueItem[]>(`${API_BASE}/deposits/open`, auth()).then(r => r.data);

// ── Manager: one deposit ───────────────────────────────────────────────────

export interface DepositDetail {
  depositId: number;
  reservationId: number;
  status: DepositStatus;
  amount: number;
  capturedAmount: number | null;
  currency: string;
  cardSavedAt: string | null;
  heldAt: string | null;
  /** The real deadline. After this the hold lapses and the money goes back regardless. */
  holdExpiresAt: string | null;
  decidedAt: string | null;
  claimReason: DepositClaimReason | null;
  /** Private to the manager. Never shown to the guest. */
  managerNote: string | null;
  /** Set when a manager let a guest in without a working deposit. */
  waivedAt: string | null;
  holdAttempts: number | null;
  lastHoldError: string | null;
}

export const getDeposit = (reservationId: number) =>
  axios.get<DepositDetail>(`${API_BASE}/reservations/${reservationId}/deposit`, auth())
    .then(r => r.data);

/**
 * Lets the whole hold go. As irreversible as claiming and more easily mistaken for the safe
 * option — a cancelled authorization cannot be placed again.
 */
export const releaseDeposit = (depositId: number) =>
  axios.post<DepositDetail>(`${API_BASE}/deposits/${depositId}/release`, {}, auth())
    .then(r => r.data);

/**
 * Keeps part of it. `amount` is what to **keep**, not what to return — the API speaks in the same
 * direction as the screen, so nothing has to subtract on the way in. The remainder releases itself
 * in the same Stripe call.
 */
export const claimDeposit = (
  depositId: number,
  body: { amount: number; reason: DepositClaimReason; managerNote?: string },
) =>
  axios.post<DepositDetail>(`${API_BASE}/deposits/${depositId}/claim`, body, auth())
    .then(r => r.data);

/** The manager's own override, for what the software cannot see. Recorded against their name. */
export const waiveDeposit = (depositId: number) =>
  axios.post<DepositDetail>(`${API_BASE}/deposits/${depositId}/waive`, {}, auth())
    .then(r => r.data);

// ── Guest ──────────────────────────────────────────────────────────────────

/**
 * Deliberately thin, and it **outlives the check-in guide**: the guide closes a day after
 * check-out, but money can still be held for days after that. Carries the deposit and nothing
 * else — no address, no door code — so a forwarded link leaks nothing.
 */
export interface PublicDepositStatus {
  required: boolean;
  status: DepositStatus | null;
  amount: number | null;
  capturedAmount: number | null;
  currency: string;
  apartmentName: string;
  checkInDate: string;
  checkOutDate: string;
  /** When the guest can expect it back — the manager's deadline, not the hold's own expiry. */
  expectedBackBy: string | null;
  decidedAt: string | null;
  claimReason: DepositClaimReason | null;
  /** True while the card step is outstanding — which is what withholds the entry code. */
  needsCard: boolean;
}

export const getPublicDeposit = (formToken: string) =>
  axios.get<PublicDepositStatus>(`${API_BASE}/public/checkin/${formToken}/deposit`)
    .then(r => r.data);

/** Opens Stripe Checkout in `setup` mode — a card is saved, no money moves. */
export const startDepositCardSetup = (formToken: string) =>
  axios.post<{ checkoutUrl: string }>(
    `${API_BASE}/public/checkin/${formToken}/deposit/card-session`, {})
    .then(r => r.data.checkoutUrl);
